import { useEffect, useState } from "react";
import { attachmentObjectUrl } from "../lib/api";
import type { AttachmentRow } from "../lib/types";

/** Leading glyph for an attachment row in a file list. For images it lazily
 *  fetches an authed object URL and renders a small thumbnail (click to preview
 *  full-size in a lightbox dialog); for everything else (and while/if the image
 *  fails to load) it shows the extension badge — the same `.att-ext` chip used
 *  before thumbnails existed. Reused by every attachment list, so the preview
 *  works on tickets, tasks, and facility work orders alike. */
export function AttachmentThumb({ att }: { att: AttachmentRow }) {
  const isImage = (att.content_type || "").toLowerCase().startsWith("image/");
  const ext = (att.filename.split(".").pop() || "").slice(0, 4).toUpperCase() || "FILE";
  const [url, setUrl] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let made: string | null = null;
    attachmentObjectUrl(att.id)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        made = u;
        setUrl(u);
      })
      .catch(() => {
        /* fall back to the ext badge */
      });
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
  }, [att.id, isImage]);

  // Esc closes the lightbox while it's open.
  useEffect(() => {
    if (!preview) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPreview(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [preview]);

  if (isImage && url) {
    return (
      <>
        <button
          type="button"
          className="att-thumb att-thumb-btn"
          onClick={(e) => {
            e.stopPropagation();
            setPreview(true);
          }}
          aria-label={`Preview ${att.filename}`}
          title="Click to preview"
        >
          <img src={url} alt={att.filename} loading="lazy" />
        </button>
        {preview ? (
          <div
            className="img-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={att.filename}
            onClick={() => setPreview(false)}
          >
            <button className="img-lightbox-close" type="button" aria-label="Close preview" onClick={() => setPreview(false)}>
              ✕
            </button>
            <figure className="img-lightbox-figure" onClick={(e) => e.stopPropagation()}>
              <img src={url} alt={att.filename} />
              <figcaption>{att.filename}</figcaption>
            </figure>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <span className="att-ext" data-ext={ext.toLowerCase()}>
      {ext}
    </span>
  );
}
